import { Button, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronUp20Regular } from '@fluentui/react-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGridState } from '../../../hooks/useGridState';
import { useSearchTable } from '../../../hooks/useSearchTable';
import { useVarianceNavigation } from '../../../hooks/useVarianceNavigation';
import { ReportVariancePoint } from '../../../model';
import { customPalette } from '../../../theme';

interface IVarianceGoToNavigationProps {
    variancePoints?: ReportVariancePoint[];
    isDisabled?: boolean;
    showOnlyExceedingRows: boolean;
}

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: tokens.fontSizeBase300,
        padding: '4px 0px',
        borderRadius: tokens.borderRadiusMedium,
        columnGap: '20px',
        height: '32px',
    },
    rootEnabled: {
        ...shorthands.border(tokens.strokeWidthThin, 'solid', customPalette.blue075A92),
        backgroundColor: '#FFFFFF',
        '& > span': {
            color: customPalette.blueDark,
        },
        '& button svg': {
            color: customPalette.blueDark,
        },
    },
    rootDisabled: {
        ...shorthands.border(tokens.strokeWidthThin, 'solid', customPalette.disabledGreyBorder),
        backgroundColor: customPalette.disabledGrey,
        '& > span': {
            color: customPalette.cementGrey,
        },
        '& button svg': {
            color: customPalette.cementGrey,
        },
    },
    label: {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 'auto',
        padding: '5px 10px',
        alignContent: 'center',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
    },
    labelWithButtons: {
        minWidth: '100px',
    },
    buttons: {
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 0,
        flexShrink: 0,
        justifyContent: 'flex-end',
    },
});

const VarianceGoToNavigation: React.FC<IVarianceGoToNavigationProps> = (props: IVarianceGoToNavigationProps) => {
    const { variancePoints, isDisabled } = props;
    const { t } = useTranslation();
    const styles = useStyles();
    const { isSearchValueApplied } = useSearchTable();
    const { grid } = useGridState();

    const { isVarianceNavigating, currentVarianceIndex, scrollToVariance } = useVarianceNavigation();

    const hasVariances = useMemo(() => Boolean(variancePoints && variancePoints.length > 0), [variancePoints]);
    const areButtonsDisabled = Boolean(isDisabled || isVarianceNavigating);

    const handlePrevious = () => {
        scrollToVariance(variancePoints || [], true, props.showOnlyExceedingRows ?? false, grid?.current);
    };
    const handleNext = () => {
        scrollToVariance(variancePoints || [], false, props.showOnlyExceedingRows ?? false, grid?.current);
    };

    const shouldRenderResultsAsXofY = () =>
        variancePoints?.length && currentVarianceIndex !== undefined && currentVarianceIndex > 0 && !isSearchValueApplied;

    const getVarianceResultCountLabel = () => {
        if (shouldRenderResultsAsXofY()) {
            return `${currentVarianceIndex} of ${variancePoints?.length}`;
        }
        if (hasVariances) {
            return `${variancePoints?.length} ${t('results')}`;
        }
        return t('noResults').toString();
    };

    return (
        <div className={mergeClasses(styles.root, isDisabled ? styles.rootDisabled : styles.rootEnabled)}>
            <span
                aria-label={`${getVarianceResultCountLabel()} ${t('ofVariance')}`}
                className={mergeClasses(styles.label, hasVariances && styles.labelWithButtons)}
            >
                {getVarianceResultCountLabel()}
            </span>
            {hasVariances && (
                <div className={styles.buttons}>
                    <Button
                        size='small'
                        appearance='subtle'
                        disabled={areButtonsDisabled}
                        onClick={handleNext}
                        icon={<ChevronUp20Regular />}
                        aria-label={t('previousVariance').toString()}
                    />
                    <Button
                        size='small'
                        appearance='subtle'
                        disabled={areButtonsDisabled}
                        onClick={handlePrevious}
                        icon={<ChevronDown20Regular />}
                        aria-label={t('nextVariance').toString()}
                    />
                </div>
            )}
        </div>
    );
};

export default VarianceGoToNavigation;
